import { createAsyncThunk } from "@reduxjs/toolkit";
import { instance } from "src/services/api-client";
import { NotificationService } from "src/helpers/notifications";
import {
  successfulResponsesTrns,
  pendingResponsesTrns,
  errorResponsesTrns,
} from "src/translate/responses";
import { IResponseWithPagination } from "src/@types/api";
import { IClone } from "src/@types/clones";

export const FAVOURITES_SLICE_NAME = "favorites";
export const ITEMS_PER_PAGE = 10;

interface Params {
  filters: string;
  page?: number;
}

interface FavoritesAsyncParams {
  id: number;
  onSuccess?: () => void;
}

export const getFavoritesAsync = createAsyncThunk(
  `${FAVOURITES_SLICE_NAME}/fetchFavorites`,
  async ({ filters = "" }: Params, { rejectWithValue }) => {
    try {
      const filtersQueryStr = filters ? filters : "page=1";

      const { data } = await instance.get<IResponseWithPagination<IClone>>(
        `/favorite/?${filtersQueryStr}&page_size=${ITEMS_PER_PAGE}`
      );

      return data;
    } catch ({ response }) {
      const errorText = response?.data?.error;

      NotificationService.error(
        errorText || errorResponsesTrns.gettingFavorites
      );

      return rejectWithValue(errorText);
    }
  }
);

export const addToFavoritesAsync = createAsyncThunk(
  `${FAVOURITES_SLICE_NAME}/fetchAddToFavorites`,
  async (
    { id, onSuccess = () => {} }: FavoritesAsyncParams,
    { rejectWithValue }
  ) => {
    const toastId = NotificationService.loading(
      pendingResponsesTrns.addingFavorite
    );

    try {
      const { data } = await instance.post<IClone>("/favorite/", {
        clone: id,
      });

      NotificationService.updateToSuccess(
        toastId,
        successfulResponsesTrns.addingFavorite
      );

      onSuccess();

      return data;
    } catch ({ response }) {
      const errorText = response?.data?.error;

      NotificationService.updateToError(
        toastId,
        errorText || errorResponsesTrns.addingFavorite
      );

      return rejectWithValue(errorText);
    }
  }
);

export const removeFromFavoritesAsync = createAsyncThunk(
  `${FAVOURITES_SLICE_NAME}/fetchRemoveFromFavorites`,
  async (
    { id, onSuccess = () => {} }: FavoritesAsyncParams,
    { rejectWithValue }
  ) => {
    const toastId = NotificationService.loading(
      pendingResponsesTrns.removingFavorite
    );

    try {
      await instance.delete(`/favorite/${id}/`);

      NotificationService.updateToSuccess(
        toastId,
        successfulResponsesTrns.removingFavorite
      );

      onSuccess();

      return id;
    } catch ({ response }) {
      const errorText = response?.data?.error;

      NotificationService.updateToError(
        toastId,
        errorText || errorResponsesTrns.removingFavorite
      );

      return rejectWithValue(errorText);
    }
  }
);
